import { trackingFields } from 'client/Global/AnalyticsEvents'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { SubscriptionInterface } from 'client/Interfaces/Subscription'
import { isItAnActiveSubscription } from 'client/reducers/Subscription/companySubscription'
import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import { navigateRemoveModal } from '../../actions/navigation'
import { Analytics } from '../../Global/Analytics'
import Button, { ButtonType } from '../common/Button/Button'
import { Modal } from '../common/Modal/Modal'
import {
  BankingIntermediary,
  ConsumablesIntermediary,
  CustomerIntermediary,
  DocumentCustomisationIntermediary,
  EInvoiceIntermediary,
  EmailAttachmentIntermediary,
  EstimateToInvoiceIntermediary,
  FreeInvoiceLimitIntermediary,
  IntermediaryConfigInterface,
  IntermediaryConfigProps,
  PaperInvoiceIntermediary,
  PaymentScheduleIntermediary,
  RecurringProfileIntermediary,
  ReminderIntermediary,
  ReminderPostIntermediary,
  TrackerIntermediary,
  TradenamesIntermediary,
} from './components/PremiumFeatureIntermediaryConfigs'

export enum PremiumFeatureType {
  PAPER = 'PAPER',
  EINVOICE = 'EINVOICE',
  REMINDER = 'REMINDER',
  REMINDER_POST = 'REMINDER_POST',
  TRACKER = 'TRACKER',
  TRADENAMES = 'TRADENAMES',
  ESTIMATE_TO_INVOICE = 'ESTIMATE_TO_INVOICE',
  CUSTOMERS = 'CUSTOMERS',
  PAYMENT_SCHEDULE = 'PAYMENT_SCHEDULE',
  EMAIL_ATTACHMENT = 'EMAIL_ATTACHMENT',
  RECURRING_PROFILE = 'RECURRING_PROFILE',
  BANKING = 'BANKING',
  DOCUMENT_CUSTOMISATION = 'DOCUMENT_CUSTOMISATION',
  CONSUMABLES = 'CONSUMABLES',
  FREE_INVOICE_LIMIT = 'FREE_INVOICE_LIMIT',
}
export interface IIntermediaryContext {
  documentId?: string
}
interface ExternalProps {
  index: number
  context?: IIntermediaryContext
  analyticsPlacement: string
  isOpen: boolean
  blocking?: boolean
  featureType: PremiumFeatureType
  next?: () => void
  onClose?: () => void
}

interface ActionProps {
  actions: {
    close: () => void
  }
}
interface IStateProps {
  isActiveSubscription?: boolean
  subscription?: SubscriptionInterface
}

export type PremiumFeatureIntermediaryModalProps = ExternalProps & ActionProps & IStateProps

const className = 'PremiumFeatureIntermediaryModal'

export class PremiumFeatureIntermediaryClass extends React.Component<
  PremiumFeatureIntermediaryModalProps,
  IntermediaryConfigInterface
> {
  private skipRender: boolean
  constructor(props) {
    super(props)
    // skip rendering the window if it's not a new user
    if (this.props.isActiveSubscription && this.props.subscription.plan.name !== 'plan.starter') {
      this.props.actions.close()
      this.props.next()
      this.skipRender = true
      return
    }
    this.skipRender = false
    this.state = null
  }

  private static getConfig(
    feature: PremiumFeatureType,
    subscription: SubscriptionInterface,
    context?: IIntermediaryContext,
  ): IntermediaryConfigInterface {
    context = context || {}
    const props: IntermediaryConfigProps = { className, subscription, context }
    switch (feature) {
      case PremiumFeatureType.EINVOICE:
        return EInvoiceIntermediary(props)
      case PremiumFeatureType.REMINDER:
        return ReminderIntermediary(props)
      case PremiumFeatureType.REMINDER_POST:
        return ReminderPostIntermediary(props)
      case PremiumFeatureType.TRACKER:
        return TrackerIntermediary(props)
      case PremiumFeatureType.TRADENAMES:
        return TradenamesIntermediary(props)
      case PremiumFeatureType.ESTIMATE_TO_INVOICE:
        return EstimateToInvoiceIntermediary(props)
      case PremiumFeatureType.CUSTOMERS:
        return CustomerIntermediary(props)
      case PremiumFeatureType.PAYMENT_SCHEDULE:
        return PaymentScheduleIntermediary(props)
      case PremiumFeatureType.EMAIL_ATTACHMENT:
        return EmailAttachmentIntermediary(props)
      case PremiumFeatureType.RECURRING_PROFILE:
        return RecurringProfileIntermediary(props)
      case PremiumFeatureType.BANKING:
        return BankingIntermediary(props)
      case PremiumFeatureType.DOCUMENT_CUSTOMISATION:
        return DocumentCustomisationIntermediary(props)
      case PremiumFeatureType.CONSUMABLES:
        return ConsumablesIntermediary(props)
      case PremiumFeatureType.FREE_INVOICE_LIMIT:
        return FreeInvoiceLimitIntermediary(props)
      case PremiumFeatureType.PAPER:
      default:
        return PaperInvoiceIntermediary(props)
    }
  }

  public UNSAFE_componentWillMount() {
    if (this.skipRender) {
      return
    }

    this.setState(
      PremiumFeatureIntermediaryClass.getConfig(this.props.featureType, this.props.subscription, this.props.context),
    )
  }

  private onClose() {
    if (this.props.featureType === 'BANKING') {
      Analytics.trackEvent(trackingFields.name.bankConnectionPaywall, {
        type: trackingFields.type.action,
        category: trackingFields.category.bankConnection,
        result: trackingFields.Modifiers.cancel,
        placement: trackingFields.placement.bankConnection,
      })
    }
    if (this.state.footerButtons.analytics && this.state.footerButtons.analytics.cancel) {
      const { name, payload } = this.state.footerButtons.analytics.cancel
      Analytics.trackEvent(name, payload)
    }
    this.props.actions.close()
    if (this.props.onClose) {
      this.props.onClose()
    }
  }

  private tryForFree() {
    if (this.props.featureType === 'BANKING') {
      Analytics.trackEvent(trackingFields.name.bankConnectionPaywall, {
        type: trackingFields.type.action,
        category: trackingFields.category.bankConnection,
        result: trackingFields.Modifiers.success,
        placement: trackingFields.placement.bankConnection,
      })
    }
    if (this.state.footerButtons.analytics && this.state.footerButtons.analytics.confirm) {
      const { name, payload } = this.state.footerButtons.analytics.confirm
      Analytics.trackEvent(name, payload)
    }
    this.props.next()
    this.props.actions.close()
  }

  public render(): JSX.Element {
    if (this.skipRender) {
      return <span />
    }
    const zindex = this.props.index
    return (
      <Modal
        isOpen={this.props.isOpen}
        index={zindex}
        headerJSX={this.state.header}
        className={className}
        size={'medium'}
        showCloseIcon={!this.props.blocking}
        onClose={() => this.onClose()}
      >
        {this.state.body}
        <footer className={`${className}-footer`}>
          <Button
            type={ButtonType.accept}
            action={() => {
              this.tryForFree()
            }}
            label={this.state.footerButtons.confirm}
            automation={this.state.footerButtons.automations.confirm}
          />
          <Button
            type={ButtonType.cancel}
            action={() => this.onClose()}
            label={this.state.footerButtons.cancel}
            automation={this.state.footerButtons.automations.cancel}
          />
        </footer>
      </Modal>
    )
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): ActionProps => ({
  actions: {
    close: () => dispatch(navigateRemoveModal('PremiumFeatureIntermediaryModal')),
  },
})

const mapStateToProps = (state: StateInterface): IStateProps => ({
  isActiveSubscription: isItAnActiveSubscription(state),
  subscription: state.subscriptionManagement.companySubscription.subscription,
})

export default connect<IStateProps, ActionProps, ExternalProps>(
  mapStateToProps,
  mapDispatchToProps,
)(PremiumFeatureIntermediaryClass)
